import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import { paperClasses } from '@mui/material/Paper';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useTheme } from '@mui/material/styles';
import { Collapse } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Box, Flex } from '@atoms/layout';
import { Select, TextField } from '@atoms/inputs';
import { LoadingButton, Button } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';

type terminateDialogProps = Omit<DialogProps, 'onSubmit'> & {
  onSubmit?: (reason: string) => void;
  loading?: boolean;
  type: 'shift' | 'series' | 'rotation';
};
export function TerminateDialog({ sx, onSubmit, loading, type, ...props }: terminateDialogProps) {
  const theme = useTheme();

  return (
    <Dialog
      fullWidth
      maxWidth={'xs'}
      sx={{
        [`& .${paperClasses.root}`]: {
          bgcolor: `color-mix(in srgb, #fff 100%, ${theme.palette.primary.main} 8%)`,
        },
        ...sx,
      }}
      {...props}
    >
      <Content
        onCloseClick={() => props.onClose?.({}, 'escapeKeyDown')}
        onSubmit={onSubmit}
        loading={loading}
        type={type}
      />
    </Dialog>
  );
}

type ContentProps = {
  onCloseClick: () => void;
  onSubmit?: (reason: string) => void;
  loading?: boolean;
  type: 'shift' | 'series' | 'rotation';
};
function Content({ onCloseClick, onSubmit, loading = false, type }: ContentProps) {
  const intl = useIntl();
  const [reason, setReason] = useState('');
  const [reasonSelect, setReasonSelect] = useState('');
  const validCustom = reasonSelect === 'custom' ? reason !== '' : true;
  const validValue = reasonSelect !== '' && validCustom;

  return (
    <>
      <DialogTitle sx={{ px: 3, pt: 4, pb: 2 }}>
        <Flex justifyContent="space-between">
          <Flex column gap={1}>
            <Typography variant="h5">
              <FormattedMessage
                id="shifts.terminateDialog.title"
                values={{ type: <FormattedMessage id={`shifts.terminateDialog.type.${type}`} /> }}
              />
            </Typography>
            <Typography variant="body1" color="text.secondary">
              <FormattedMessage id="shifts.terminateDialog.subtitle" />
            </Typography>
          </Flex>
          <Box>
            <IconButton sx={{ width: '40px', position: 'relative', top: -5, right: -5 }} onClick={onCloseClick}>
              <FaIcon name="xmark" variant="light" />
            </IconButton>
          </Box>
        </Flex>
      </DialogTitle>
      <DialogContent>
        <Flex mt={2} column gap={2}>
          <Select
            size="small"
            value={reasonSelect}
            onChange={(e) => setReasonSelect(e.target.value)}
            fullWidth
            label={<FormattedMessage id="shifts.terminateDialog.reasonLabel" />}
            placeholder={intl.formatMessage({
              id: 'shifts.terminateDialog.reasonPlaceholder',
            })}
            options={terminateReasons.map((reason) => ({
              key: reason.value,
              ...reason,
            }))}
          />
          <Collapse in={reasonSelect === 'custom'}>
            <TextField
              value={reason}
              size="small"
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              label={<FormattedMessage id="shifts.terminateDialog.reasonLabel" />}
              placeholder={intl.formatMessage({
                id: 'shifts.terminateDialog.reasonPlaceholder',
              })}
            />
          </Collapse>
        </Flex>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
        <Box color="text.secondary">
          <Button variant="text" color="inherit" rounded onClick={onCloseClick} size="large">
            <FormattedMessage id="common.cancel" />
          </Button>
        </Box>
        <LoadingButton
          loading={loading}
          variant="contained"
          rounded
          onClick={() => onSubmit?.(reasonSelect === 'custom' ? reason : reasonSelect)}
          size="large"
          disabled={!validValue}
        >
          <FormattedMessage id="common.submit" />
        </LoadingButton>
      </DialogActions>
    </>
  );
}

const terminateReasons = [
  {
    label: <FormattedMessage id={`shifts.terminateDialog.reason.noLongerNeeded`} />,
    value: 'noLongerNeeded',
  },
  {
    label: <FormattedMessage id={`shifts.terminateDialog.reason.sick`} />,
    value: 'sick',
  },
  {
    label: <FormattedMessage id={`shifts.terminateDialog.reason.emergency`} />,
    value: 'emergency',
  },
  {
    label: <FormattedMessage id={`shifts.terminateDialog.reason.custom`} />,
    value: 'custom',
  },
];
